import React, { useState } from 'react'
import { Box, InputBase, IconButton, CircularProgress, Typography } from '@mui/material'
import { styled, alpha } from '@mui/material/styles'
import { KeyboardArrowUp } from '@mui/icons-material'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { useLocalStorage } from '@/hooks/common'

const ModernInputContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '16px',
  backgroundColor: '#ffffff',
})

const UserAvatarStyled = styled(Box)({
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #c084fc 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 2px 6px rgba(139, 92, 246, 0.2)',
  border: '2px solid #ffffff',

  '& p': {
    fontSize: '12px',
    fontWeight: 600,
    color: '#ffffff',
    margin: 0,
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  },
})

const InputWrapper = styled(Box)({
  flex: 1,
  backgroundColor: '#f3f4f6',
  borderRadius: '20px',
  padding: '10px 16px',

  '&:focus-within': {
    backgroundColor: '#ffffff',
    outline: '2px solid #8b5cf6',
    outlineOffset: '-2px',
  },
})

const StyledInputBase = styled(InputBase)({
  width: '100%',
  fontSize: '14px',
  fontWeight: 400,
  color: '#374151',

  '& .MuiInputBase-input': {
    padding: '0',

    '&::placeholder': {
      color: '#9ca3af',
      opacity: 1,
    },
  },
})

const SendButton = styled(IconButton)<{ hasText: boolean }>(({ hasText }) => ({
  width: '28px',
  height: '28px',
  padding: 0,
  backgroundColor: hasText ? '#8b5cf6' : '#e5e7eb',
  borderRadius: '50%',
  flexShrink: 0,
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: hasText ? '#7c3aed' : '#d1d5db',
    transform: hasText ? 'scale(1.05)' : 'none',
  },

  '& svg': {
    fontSize: '16px',
    color: hasText ? '#ffffff' : '#9ca3af',
    transition: 'color 0.2s ease',
  },

  '&:disabled': {
    backgroundColor: '#e5e7eb',
    transform: 'none',

    '& svg': {
      color: '#9ca3af',
    },
  },
}))

interface ModernCommentInputProps {
  value: string
  placeholder?: string
  disabled?: boolean
  isLoading?: boolean
  maxLength?: number
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onSubmit: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

export function ModernCommentInput({
  value,
  placeholder = 'Reply...',
  disabled = false,
  isLoading = false,
  maxLength = 500,
  onChange,
  onSubmit,
  onKeyDown,
}: ModernCommentInputProps) {
  const canSubmit = value.trim().length > 0 && !disabled && !isLoading
  const hasText = value.trim().length > 0

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (canSubmit) {
        onSubmit()
      }
    }
    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  return (
    <ModernInputContainer>
      <InputWrapper>
        <StyledInputBase
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          inputProps={{ maxLength }}
        />
      </InputWrapper>

      <SendButton hasText={hasText} onClick={onSubmit} disabled={!canSubmit}>
        {isLoading ? (
          <CircularProgress size={14} sx={{ color: hasText ? '#ffffff' : '#9ca3af' }} />
        ) : (
          <KeyboardArrowUp />
        )}
      </SendButton>
    </ModernInputContainer>
  )
}
